import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface XeroTenant {
  tenant_id: string;
  tenant_name: string | null;
  tenant_type: string | null;
  connection_id: string | null;
}

export interface XeroOrgInfo {
  name: string | null;
  legal_name: string | null;
  base_currency: string | null;
  country_code: string | null;
  short_code: string | null;
  tax_number: string | null;
  organisation_type: string | null;
  timezone: string | null;
}

export interface XeroProviderMetadata {
  tenant_id: string | null;
  tenant_name: string | null;
  available_tenants: XeroTenant[];
  org: XeroOrgInfo | null;
  xero_user_email: string | null;
}

export interface XeroIntegration {
  id: string;
  status: 'connected' | 'expired' | 'revoked' | 'error';
  status_message: string | null;
  connected_at: string;
  last_refreshed_at: string | null;
  last_error: string | null;
  last_error_at: string | null;
  access_token_expires_at: string | null;
  token_valid: boolean;
  metadata: XeroProviderMetadata;
}

export interface XeroAccountOption {
  account_id: string;
  code: string | null;
  name: string;
  type: string;
  tax_type: string | null;
  enable_payments: boolean;
  status: string | null;
  bank_account_type: string | null;
}
export interface XeroTaxRateOption {
  tax_type: string;
  name: string;
  effective_rate: number;
  status: string | null;
  can_apply_to_revenue: boolean;
}
export interface XeroBrandingTheme {
  branding_theme_id: string;
  name: string;
}
export interface XeroCatalog {
  accounts: XeroAccountOption[];
  tax_rates: XeroTaxRateOption[];
  branding_themes: XeroBrandingTheme[];
  fetched_at: string;
}

export type XeroPaymentMethod = 'cash' | 'card' | 'bank_transfer' | 'other' | 'square';
export const XERO_PAYMENT_METHODS: XeroPaymentMethod[] = ['cash', 'card', 'bank_transfer', 'other', 'square'];

export interface XeroSettings {
  company_id: string;
  tenant_id: string;
  tenant_name: string | null;
  base_currency: string | null;
  short_code: string | null;
  sales_account_code: string | null;
  tax_type_taxable: string | null;
  tax_type_exempt: string | null;
  payment_accounts: Partial<Record<XeroPaymentMethod, string>>;
  square_fee_account_code: string | null;
  branding_theme_id: string | null;
  push_status: 'AUTHORISED' | 'DRAFT';
  pull_contacts: boolean;
  sync_from_date: string | null;
  setup_complete: boolean;
}

export interface XeroSettingsOptions {
  org: XeroOrgInfo | null;
  tenant_name: string | null;
  catalog: XeroCatalog;
  settings: XeroSettings | null;
  suggested: {
    sales_account_code: string | null;
    tax_type_taxable: string | null;
    tax_type_exempt: string | null;
    payment_account: string | null;
  };
}

export interface XeroSettingsInput {
  sales_account_code: string | null;
  tax_type_taxable: string | null;
  tax_type_exempt: string | null;
  payment_accounts: Partial<Record<XeroPaymentMethod, string>>;
  branding_theme_id: string | null;
  push_status: 'AUTHORISED' | 'DRAFT';
  pull_contacts: boolean;
}

export interface XeroSyncStats {
  days: number;
  queue: {
    queued: number;
    running: number;
    done: number;
    failed: number;
    dead: number;
    skipped: number;
    last_pushed_at: string | null;
  };
  links: {
    contacts: number;
    invoices: number;
    payments: number;
    credit_notes: number;
    drift: number;
    locked: number;
    orphaned: number;
  };
  poll: {
    enabled: boolean;
    next_pull_at: string | null;
    pull_requested: boolean;
    running_mode: string | null;
    running_since: string | null;
    last_run_at: string | null;
    last_run_mode: string | null;
    last_run_status: string | null;
    last_run_error: string | null;
    last_run_processed: number | null;
    consecutive_failures: number;
    min_limit_remaining: number | null;
    day_limit_remaining: number | null;
    rate_limited_until: string | null;
  } | null;
}

export interface XeroSyncIssue {
  kind: 'queue' | 'link';
  id: string;
  entity_type: string;
  entity_id: string;
  label: string | null;
  state: string;
  detail: string | null;
  error_kind: string | null;
  attempts: number;
  xero_id: string | null;
  occurred_at: string;
}

const STATUS_KEY = ['integration-status', 'xero'] as const;
const OPTIONS_KEY = ['xero-settings-options'] as const;
const STATS_KEY = ['xero-sync-stats'] as const;
const ISSUES_KEY = ['xero-sync-issues'] as const;

/** Sentinel returned by the status query when the RPC refuses a non-admin. */
const FORBIDDEN = Symbol('xero-status-forbidden');
type StatusResult = XeroIntegration | null | typeof FORBIDDEN;

function isPermissionError(error: { message?: string } | null): boolean {
  const msg = error?.message ?? '';
  return /only admins/i.test(msg) || /permission denied/i.test(msg);
}

/**
 * supabase-js masks any non-2xx edge response as a generic message; the real
 * reason is in error.context (a Response). Pull it out.
 */
export async function edgeErrorMessage(error: unknown, fallback: string): Promise<string> {
  const err = error as { message?: string; context?: Response } | null;
  const ctx = err?.context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const body = (await ctx.clone().json()) as { error?: string; reason?: string; message?: string };
      const text = body.error || body.message || body.reason;
      if (text) return String(text);
    } catch {
      // not JSON
    }
  }
  return err?.message || fallback;
}

/**
 * Bring-your-own Xero connection (two-way accounting sync).
 *
 * Mirrors useSquareConnection. The status RPC is admin-only and throws for
 * everyone else; that case is surfaced as `isForbidden` (with a null
 * integration) instead of an error so non-admin screens stay quiet.
 */
export function useXeroConnection() {
  const queryClient = useQueryClient();

  const statusQuery = useQuery<StatusResult>({
    queryKey: STATUS_KEY,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_company_integration_status', { p_provider: 'xero' });
      if (error) {
        if (isPermissionError(error)) return FORBIDDEN;
        throw error;
      }
      const row = (data ?? [])[0];
      if (!row) return null;
      const m = (row.provider_metadata ?? {}) as Partial<XeroProviderMetadata> & Record<string, unknown>;
      return {
        id: row.id,
        status: row.status as XeroIntegration['status'],
        status_message: row.status_message,
        connected_at: row.connected_at,
        last_refreshed_at: row.last_refreshed_at,
        last_error: row.last_error,
        last_error_at: row.last_error_at,
        access_token_expires_at: row.access_token_expires_at,
        token_valid: Boolean(row.token_valid),
        metadata: {
          tenant_id: (m.tenant_id as string | null) ?? null,
          tenant_name: (m.tenant_name as string | null) ?? null,
          available_tenants: Array.isArray(m.available_tenants) ? (m.available_tenants as XeroTenant[]) : [],
          org: (m.org as XeroOrgInfo | null) ?? null,
          xero_user_email: (m.xero_user_email as string | null) ?? null,
        },
      };
    },
    retry: (failureCount, error) => !isPermissionError(error as { message?: string }) && failureCount < 2,
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: STATUS_KEY });
    queryClient.invalidateQueries({ queryKey: OPTIONS_KEY });
    queryClient.invalidateQueries({ queryKey: STATS_KEY });
    queryClient.invalidateQueries({ queryKey: ISSUES_KEY });
  };

  // Initiate the OAuth flow. On success the browser is redirected to Xero; the
  // user returns via /auth/xero/callback which forwards the code to
  // xero-oauth-callback and bounces back to /settings?xero=... for the panel.
  const connectMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('xero-oauth-initiate', {
        body: { redirect_origin: window.location.origin },
      });
      if (error) throw new Error(await edgeErrorMessage(error, 'Failed to start Xero connection'));
      const url = (data as { url?: string })?.url;
      if (!url) throw new Error('Server did not return an authorization URL');
      window.location.href = url;
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('xero-disconnect', { body: {} });
      if (error) throw new Error(await edgeErrorMessage(error, 'Failed to disconnect'));
      return data as { ok: boolean; removed_at_xero: number };
    },
    onSuccess: invalidateAll,
  });

  const setActiveTenantMutation = useMutation({
    mutationFn: async (tenantId: string) => {
      const { data, error } = await supabase.functions.invoke('xero-finalize-connect', {
        body: { tenant_id: tenantId },
      });
      if (error) throw new Error(await edgeErrorMessage(error, 'Failed to save organisation selection'));
      return data as { ok: boolean; tenant_id: string; tenant_name: string | null; base_currency: string | null };
    },
    onSuccess: invalidateAll,
  });

  const isForbidden = statusQuery.data === FORBIDDEN;
  const integration: XeroIntegration | null =
    statusQuery.data && statusQuery.data !== FORBIDDEN ? statusQuery.data : null;

  return {
    integration,
    isForbidden,
    isLoading: statusQuery.isLoading,
    refetch: statusQuery.refetch,
    connect: connectMutation.mutateAsync,
    isConnecting: connectMutation.isPending,
    disconnect: disconnectMutation.mutateAsync,
    isDisconnecting: disconnectMutation.isPending,
    setActiveTenant: setActiveTenantMutation.mutateAsync,
    isSettingTenant: setActiveTenantMutation.isPending,
  };
}

/** Live Xero chart of accounts / tax rates / themes + the saved mapping. Admin only. */
export function useXeroSettingsOptions(enabled: boolean) {
  return useQuery<XeroSettingsOptions>({
    queryKey: OPTIONS_KEY,
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('xero-settings', { body: { action: 'options' } });
      if (error) throw new Error(await edgeErrorMessage(error, 'Could not load Xero accounts'));
      return data as XeroSettingsOptions;
    },
    retry: 1,
  });
}

export function useSaveXeroSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (settings: XeroSettingsInput) => {
      const { data, error } = await supabase.functions.invoke('xero-settings', { body: { action: 'save', settings } });
      if (error) throw new Error(await edgeErrorMessage(error, 'Could not save Xero settings'));
      return data as { ok: boolean; settings: XeroSettings | null; setup_complete: boolean };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: OPTIONS_KEY });
      queryClient.invalidateQueries({ queryKey: STATUS_KEY });
      queryClient.invalidateQueries({ queryKey: STATS_KEY });
    },
  });
}

export function useXeroSyncStats(enabled: boolean, days = 7) {
  return useQuery<XeroSyncStats>({
    queryKey: [...STATS_KEY, days],
    enabled,
    refetchInterval: enabled ? 60_000 : false,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_xero_sync_stats', { p_days: days });
      if (error) throw error;
      return data as unknown as XeroSyncStats;
    },
  });
}

export function useXeroSyncIssues(enabled: boolean, limit = 50) {
  return useQuery<XeroSyncIssue[]>({
    queryKey: [...ISSUES_KEY, limit],
    enabled,
    refetchInterval: enabled ? 60_000 : false,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_xero_sync_issues', { p_limit: limit });
      if (error) throw error;
      return (data ?? []) as unknown as XeroSyncIssue[];
    },
  });
}

export interface XeroBackfillResult {
  dry_run: boolean;
  from_date: string;
  invoices: number;
  payments: number;
  credit_notes: number;
  already_in_xero: number;
  queued: number;
  eta_minutes: number;
}

/** "Sync history from <date>": dry run for counts, then the real enqueue. */
export function useXeroBackfill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ fromDate, dryRun }: { fromDate: string; dryRun: boolean }) => {
      const { data, error } = await supabase.rpc('request_xero_backfill', { p_from_date: fromDate, p_dry_run: dryRun });
      if (error) throw error;
      return data as unknown as XeroBackfillResult;
    },
    onSuccess: (result) => {
      if (!result.dry_run) {
        queryClient.invalidateQueries({ queryKey: STATS_KEY });
        queryClient.invalidateQueries({ queryKey: OPTIONS_KEY });
      }
    },
  });
}

export function useRetryXeroItems() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const { data, error } = await supabase.rpc('retry_xero_sync_items', { p_ids: ids });
      if (error) throw error;
      return Number(data ?? 0);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ISSUES_KEY });
      queryClient.invalidateQueries({ queryKey: STATS_KEY });
    },
  });
}
